import React from "react";
import { Play, Star, ExternalLink } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";

const videos = [
  {
    name: "John's Journey",
    condition: "Chronic Pain",
    duration: "Patient for 2 years",
    image: "https://images.unsplash.com/photo-1611690061822-b707a67bfebb?w=800&auto=format&fit=crop&q=60",
  },
  {
    name: "Maria's Story",
    condition: "Anxiety & Insomnia",
    duration: "Patient for 1 year",
    image: "https://images.unsplash.com/photo-1758691463582-11aea602cd4a?w=800&auto=format&fit=crop&q=60",
  },
  {
    name: "Robert's Recovery",
    condition: "Arthritis",
    duration: "Patient for 3 years",
    image: "https://images.unsplash.com/photo-1758691463393-a2aa9900af8a?w=800&auto=format&fit=crop&q=60",
  },
  {
    name: "Linda's Experience",
    condition: "Fibromyalgia",
    duration: "Patient for 6 months",
    image: "https://images.unsplash.com/photo-1723649379227-498e2ebf25c6?w=800&auto=format&fit=crop&q=60",
  },
  {
    name: "James's Path",
    condition: "Neuropathy",
    duration: "Patient for 1.5 years",
    image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=800&auto=format&fit=crop&q=60",
  },
];

const Testimonials = () => {
  return (
    <section className="bg-white" id="testimonials">

      {/* Video Testimonials Section */}
      <div className="py-20 bg-[#f8fafc]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#012d5c] mb-4">
              What Our Patients Say
            </h2>
            <p className="text-lg text-gray-600">
              Real stories from real patients.
            </p>
          </div>

          <Carousel
            opts={{
              align: "start",
              loop: false,
            }}
            className="w-full max-w-6xl mx-auto"
          >
            <CarouselContent>
              {videos.map((video, index) => (
                <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/4">
                  <div className="p-1">
                    <Card className="border-none shadow-md overflow-hidden rounded-lg group cursor-pointer hover:shadow-xl transition-all">
                      <CardContent className="p-0 relative">
                        <div className="relative aspect-[4/5] overflow-hidden">
                          <img
                            src={video.image}
                            alt={video.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                            <div className="bg-white/90 rounded-full p-4 transform transition-transform group-hover:scale-110">
                              <Play className="w-6 h-6 text-[#4caf50] fill-[#4caf50]" />
                            </div>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent text-white">
                            <h3 className="font-bold text-lg mb-1">{video.name}</h3>
                            <p className="text-sm font-medium text-[#4caf50] mb-0.5">{video.condition}</p>
                            <p className="text-xs opacity-80">{video.duration}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </div>

      {/* Google Reviews Section */}
      <div className="py-20 bg-[#012d5c] text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-[#005eb8] opacity-10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#4caf50] opacity-10 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg"
                className="w-9 h-9"
                alt="Google"
              />
            </div>
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Read Our Google Reviews
          </h2>

          <div className="flex justify-center items-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 text-[#fbbf24] fill-[#fbbf24]" />
            ))}
            <span className="text-xl font-bold ml-3 text-white">5.0</span>
            <span className="text-gray-400 ml-1 text-sm">on Google</span>
          </div>

          <p className="text-gray-300 max-w-xl mx-auto mb-10 leading-relaxed">
            Our patients share their experiences on Google. Read genuine patient reviews and find out what they say about their care at Integro Clinics.
          </p>

          <a
            href="https://www.google.com/search?q=integro+medical+clinics+limited"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white text-[#012d5c] font-bold px-8 py-4 rounded-xl hover:bg-gray-100 transition-colors shadow-lg text-base"
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg"
              className="w-5 h-5"
              alt="Google"
            />
            View Our Google Reviews
            <ExternalLink className="w-4 h-4 opacity-60" />
          </a>

          <p className="text-gray-500 text-xs mt-6 max-w-sm mx-auto">
            Reviews displayed on Google reflect the genuine experiences of verified patients.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
